// Azure AI Fundamentals (AI-901) hub configuration
import flashcards from './flashcards.js';
import practice from './practice.js';
import objective from './objective.js';

const config = {
  slug: 'azure-ai-901',
  vendor: 'Microsoft Azure',
  vendorTag: 'Azure · AI Fundamentals',
  title: 'AZURE·AI',
  titleAccent: '+',
  fullTitle: 'Azure AI Fundamentals',
  code: 'AI-901',
  channelTag: 'AZAI901',
  threatPill: '● TARGET: AI-901',
  themeStorageKey: 'azai901.theme',
  storagePrefix: 'azai901',
  passPct: 70,
  practice: { totalSeconds: 45 * 60, passPct: 70 },
  objective: { totalSeconds: 30 * 60, passPct: 70 },
  domainNames: {
    D1: 'AI Concepts & Capabilities',
    D2: 'Implement AI with Foundry',
  },
  data: { flashcards, practice, objective },

  domains: [
    {
      id: 'D1', title: 'AI Concepts and Azure AI Capabilities', weight: '40–45%', weightPct: 42,
      intro: 'The larger domain — covers what AI and ML are, the responsible AI principles Microsoft applies to Azure services, and the portfolio of pre-built Azure AI cognitive services. Heavy emphasis on Azure AI Foundry and what each service does.',
      sections: [
        {
          heading: 'Core AI & ML Concepts',
          items: [
            { term: 'Artificial Intelligence', desc: 'Software that simulates human-like cognitive capabilities such as perception, language, reasoning, and decision-making.' },
            { term: 'Machine Learning', desc: 'A subfield of AI where models learn patterns from data. Covers supervised, unsupervised, and reinforcement learning.' },
            { term: 'Deep Learning', desc: 'ML using multi-layer neural networks that automatically learn hierarchical feature representations.' },
            { term: 'Regression', desc: 'Predicts a continuous numeric value (e.g., price, temperature).' },
            { term: 'Classification', desc: 'Predicts a discrete category label (binary or multi-class).' },
            { term: 'Clustering', desc: 'Groups similar data points without labels. Used for customer segmentation, anomaly detection.' },
            { term: 'Computer Vision', desc: 'AI that interprets images and video — object detection, classification, OCR, facial analysis.' },
            { term: 'Natural Language Processing (NLP)', desc: 'AI that understands, generates, and translates human language.' },
            { term: 'Generative AI', desc: 'Models that create new content (text, images, code, audio) rather than merely classifying existing content.' },
          ],
        },
        {
          heading: 'Responsible AI Principles',
          items: [
            { term: 'Fairness', desc: 'AI systems should treat all people equitably and avoid introducing or amplifying bias.' },
            { term: 'Reliability & Safety', desc: 'Systems must perform as expected and have controls to prevent harmful behavior.' },
            { term: 'Privacy & Security', desc: 'Protect user data and comply with regulations like GDPR.' },
            { term: 'Inclusiveness', desc: 'AI should empower everyone, including people with disabilities.' },
            { term: 'Transparency', desc: 'People should understand how AI decisions are made.' },
            { term: 'Accountability', desc: 'Humans remain responsible for AI system decisions and outcomes.' },
          ],
        },
        {
          heading: 'Azure AI Services',
          items: [
            { term: 'Azure AI Vision', desc: 'Image Analysis, Object Detection, Face API, OCR, Image Description. Spatial Analysis for video streams.' },
            { term: 'Azure AI Speech', desc: 'Speech-to-text, text-to-speech, speaker recognition, real-time translation.' },
            { term: 'Azure AI Language', desc: 'Sentiment analysis, key phrase extraction, NER, language detection, text classification, conversational language understanding (CLU).' },
            { term: 'Azure AI Translator', desc: 'Real-time, multi-language machine translation API.' },
            { term: 'Azure AI Document Intelligence', desc: 'Form Recognizer rebranded — extracts structured data from PDFs, forms, receipts, invoices.' },
            { term: 'Azure AI Search', desc: 'Intelligent search with built-in ML enrichment pipeline (skills) and vector search for RAG architectures.' },
            { term: 'Azure AI Content Safety', desc: 'Detect and filter harmful content in text and images across configurable severity thresholds.' },
            { term: 'Azure Bot Service', desc: 'Platform for building, testing, and deploying conversational agents with Power Virtual Agents integration.' },
          ],
        },
        {
          heading: 'Azure Machine Learning',
          items: [
            { term: 'Azure ML Studio', desc: 'Web-based IDE for authoring, training, deploying, and managing ML experiments and models.' },
            { term: 'Automated ML (AutoML)', desc: 'AutoML iterates over algorithms and hyperparameters to find the best model for your data without manual tuning.' },
            { term: 'Designer', desc: 'Drag-and-drop pipeline builder for no-code ML workflows.' },
            { term: 'Compute Clusters & Instances', desc: 'Managed compute for training (clusters) and development (instances).' },
            { term: 'MLflow Integration', desc: 'Track experiments, parameters, metrics, and model artifacts.' },
            { term: 'Responsible AI Dashboard', desc: 'Visualize fairness, interpretability, error analysis, and causal insights for registered models.' },
          ],
        },
      ],
    },
    {
      id: 'D2', title: 'Implementing AI Solutions with Azure AI Foundry', weight: '55–60%', weightPct: 57,
      intro: 'The dominant domain — focuses on Azure AI Foundry as the unified development platform for generative AI. Covers the model catalog, Azure OpenAI Service, prompt flow orchestration, RAG with AI Search, safety evaluations, and enterprise deployment patterns.',
      sections: [
        {
          heading: 'Azure AI Foundry Platform',
          items: [
            { term: 'Azure AI Foundry (formerly Azure AI Studio)', desc: 'Unified portal for exploring, building, evaluating, and deploying generative AI solutions.' },
            { term: 'Hub & Project Structure', desc: 'Hub = shared infrastructure (networking, keys, compute); Project = workspace for a specific AI solution.' },
            { term: 'Model Catalog', desc: 'Library of FMs from OpenAI, Meta, Mistral, Hugging Face, Microsoft, and others. Filter by task, license, modality.' },
            { term: 'Deployments', desc: 'Serverless API (pay-per-token) or Managed Compute (dedicated instance) deployment options.' },
            { term: 'Azure AI Foundry SDK', desc: 'Python SDK and REST APIs for programmatic project, deployment, and evaluation management.' },
          ],
        },
        {
          heading: 'Azure OpenAI Service',
          items: [
            { term: 'Models Available', desc: 'GPT-4o, GPT-4, GPT-3.5-Turbo, DALL-E 3, Whisper, text-embedding-ada-002, text-embedding-3.' },
            { term: 'Completions vs. Chat Completions', desc: 'Chat Completions API uses role-based messages (system/user/assistant); preferred for conversational applications.' },
            { term: 'System Message', desc: 'The "system" role message sets model persona, tone, and behavioral constraints.' },
            { term: 'Function Calling / Tool Use', desc: 'Structured output that maps model responses to API calls, enabling agentic workflows.' },
            { term: 'Embeddings Endpoint', desc: 'Convert text to dense vectors for semantic search and similarity computation.' },
            { term: 'DALL-E 3', desc: 'Azure-hosted image generation model; supports size, quality, and style parameters.' },
            { term: 'Whisper', desc: 'Speech-to-text model with multilingual support available through Azure OpenAI.' },
          ],
        },
        {
          heading: 'Prompt Engineering in Azure',
          items: [
            { term: 'Prompt Flow', desc: 'Visual, code-first orchestration tool for building multi-step LLM workflows with branching, loops, and tool calls.' },
            { term: 'Zero-shot, Few-shot, CoT', desc: 'Standard prompting techniques: no examples, a few examples, and step-by-step reasoning prompts.' },
            { term: 'Grounding', desc: 'Provide retrieved context in the prompt to reduce hallucination and anchor responses in facts.' },
            { term: 'Retrieval-Augmented Generation (RAG)', desc: 'Combine Azure AI Search vector index with Azure OpenAI completions for document Q&A.' },
            { term: 'Metaprompting / System Prompts', desc: 'High-level instructions that constrain model behavior, persona, and output format for all turns.' },
          ],
        },
        {
          heading: 'Content Safety & Evaluation',
          items: [
            { term: 'Azure AI Content Safety', desc: 'Hate, sexual, violence, self-harm filters with severity scores. Configured at deployment level.' },
            { term: 'Prompt Shields', desc: 'Detects and blocks prompt injection and jailbreak attempts in user inputs.' },
            { term: 'Groundedness Detection', desc: 'Scores how well a model response is grounded in the retrieved documents.' },
            { term: 'Safety Evaluations in Foundry', desc: 'Automated evaluation flows measure defect rates for violence, sexual, hate, and self-harm categories.' },
            { term: 'Quality Metrics', desc: 'Coherence, fluency, relevance, and groundedness scored by an LLM evaluator against test data.' },
          ],
        },
        {
          heading: 'Enterprise Deployment Patterns',
          items: [
            { term: 'Private Networking', desc: 'Deploy Foundry Hub and Azure OpenAI endpoints in a VNet with private endpoints to block public access.' },
            { term: 'Managed Identity', desc: 'Use system/user-assigned managed identities instead of API keys to authenticate from Azure services.' },
            { term: 'Azure Key Vault', desc: 'Store API keys, secrets, and connection strings securely; rotate without code changes.' },
            { term: 'Monitoring & Logging', desc: 'Azure Monitor + Application Insights captures latency, token usage, errors, and content safety events.' },
            { term: 'Role-Based Access Control (RBAC)', desc: 'Built-in roles (Azure AI Developer, Azure AI Inferencing Deployment Operator) gate access to projects and endpoints.' },
          ],
        },
      ],
    },
  ],

  weightingMap: [
    { id: 'D1', weight: '40–45%', title: 'AI Concepts & Capabilities', desc: 'Core AI/ML, Responsible AI, Azure AI Services portfolio. ~5 study weeks.' },
    { id: 'D2', weight: '55–60%', title: 'Implement AI with Foundry', desc: 'Azure AI Foundry, Azure OpenAI, Prompt Flow, RAG, safety. ~7 study weeks.' },
    { id: 'FINAL', weight: '100%', title: 'Mock + Review', desc: 'Practice exams + cheat sheet. Final sprint.' },
  ],
};

export default config;
